import { FileText } from 'lucide-react';

import { DetailSection } from '@/components/admin/detail-parts';
import { StatusBadge } from '@/components/ui/status-badge';
import { type AdminTalentCredential } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import {
  useCredentialKindLabel,
  useCredentialStatusLabel,
  useDocumentStatusLabel,
} from '@/lib/labels';
import { credentialTone, documentTone } from '@/lib/status';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';

export function CredentialsSection({
  credentials,
}: {
  credentials: readonly AdminTalentCredential[];
}) {
  const t = useTranslations('admin.talentDetail');
  const documents = useTranslations('admin.documents');
  const kindLabel = useCredentialKindLabel();
  const credentialStatusLabel = useCredentialStatusLabel();
  const documentStatusLabel = useDocumentStatusLabel();

  const columns: ReadonlyArray<DataTableColumn<AdminTalentCredential>> = [
    {
      key: 'title',
      header: t('credentials.title'),
      cell: (credential) => (
        <>
          <p className="font-semibold text-navy" dir="auto">
            {credential.title}
          </p>
          <p className="text-[11.5px] text-conc-500">{kindLabel(credential.kind)}</p>
        </>
      ),
    },
    {
      key: 'issuer',
      header: t('credentials.issuer'),
      cellClassName: 'text-conc-600',
      // An achievement may name no awarding body; say so rather than leave a gap.
      cell: (credential) =>
        credential.issuer ? (
          <span dir="auto">{credential.issuer}</span>
        ) : (
          <span className="text-conc-400">{documents('unknownIssuer')}</span>
        ),
    },
    {
      key: 'dates',
      header: t('credentials.dates'),
      cellClassName: 'num text-conc-600',
      cell: (credential) => <CredentialDates credential={credential} />,
    },
    {
      key: 'status',
      header: t('credentials.status'),
      cell: (credential) => (
        <StatusBadge tone={credentialTone(credential.status)}>
          {credentialStatusLabel(credential.status)}
        </StatusBadge>
      ),
    },
    {
      key: 'document',
      header: t('credentials.document'),
      cell: (credential) => (
        <StatusBadge tone={documentTone(credential.documentStatus)}>
          {documentStatusLabel(credential.documentStatus)}
        </StatusBadge>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('credentials.actions')}</span>,
      headClassName: 'text-end',
      cellClassName: 'text-end',
      cell: (credential) =>
        credential.previewUrl ? (
          <a
            href={credential.previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-pitch-700 hover:underline"
          >
            <FileText aria-hidden className="h-3.5 w-3.5" />
            {t('credentials.viewDocument')}
          </a>
        ) : (
          <span className="text-conc-300">—</span>
        ),
    },
  ];

  return (
    <DetailSection title={t('section.credentials')}>
      {credentials.length === 0 ? (
        <p className="text-[12.5px] text-conc-400">{t('credentials.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable
            inline
            caption={t('section.credentials')}
            rows={credentials}
            columns={columns}
            rowKey={(credential) => credential.id}
          />
        </div>
      )}
    </DetailSection>
  );
}

export function CredentialDates({ credential }: { credential: AdminTalentCredential }) {
  const t = useTranslations('admin.talentDetail');
  if (credential.startedOn) {
    const end = credential.isCurrent ? t('credentials.current') : isoDate(credential.endedOn);
    return (
      <>
        {isoDate(credential.startedOn)}
        {end && ` – ${end}`}
      </>
    );
  }
  return <>{isoDate(credential.issuedOn) ?? '—'}</>;
}
